import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';

import buildReason from './buildReason';
import { HealthBar } from './StatusBreakdown';
import { subtitle, statusLabel, healthRowsPropType } from './shared';
import {
  HeatLegend,
  HeatLayout,
  HeatGrid,
  Cell,
  HeatDetail,
  DRow,
  CardLink,
  Pill,
  Skeleton,
} from './style';
import { timeAgo } from '../../utils/dates';

const mini = (a) => {
  if (a.status === 'healthy') return `${a.succeeded} ✓`;
  if (a.status === 'idle') return '—';
  return `${a.failed} ✗`;
};

// Colored square tying a detail row to its health-bar segment. Blocked reads blue
// (blocked-by-condition / information), matching the bar's non-error blocked band.
const swatch = (bg) => (
  <i
    style={{
      width: 9,
      height: 9,
      borderRadius: 2,
      background: bg,
      display: 'inline-block',
      marginRight: 8,
      flexShrink: 0,
    }}
  />
);

export default function HealthHeatmap({ rows, loading, emptyState }) {
  const [selectedId, setSelectedId] = useState(null);

  if (loading) {
    return (
      <HeatGrid>
        {['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'].map((k) => (
          <Cell key={k} as="div">
            <Skeleton style={{ width: '80%' }} />
          </Cell>
        ))}
      </HeatGrid>
    );
  }
  if (!rows.length) return emptyState;

  const selected = rows.find((r) => r.id === selectedId) || rows[0];
  const a = selected.attributes || {};

  return (
    <>
      <HeatLegend>
        <span>
          <i style={{ background: 'var(--danger-bright)' }} />
          {t`Unhealthy`}
        </span>
        <span>
          <i style={{ background: 'var(--warn-bright)' }} />
          {t`Degraded`}
        </span>
        <span>
          <i style={{ background: 'var(--good-bright)' }} />
          {t`Healthy`}
        </span>
        <span>
          <i style={{ background: 'var(--idle-bright)' }} />
          {t`No Activity`}
        </span>
      </HeatLegend>
      <HeatLayout>
        <HeatGrid>
          {rows.map((r) => {
            const ra = r.attributes || {};
            return (
              <Cell
                key={r.id}
                type="button"
                className={`${ra.status}${selected.id === r.id ? ' sel' : ''}`}
                onClick={() => setSelectedId(r.id)}
                title={ra.name}
              >
                <span className="nm">{ra.name}</span>
                <span className="foot">
                  <span className="mini">{mini(ra)}</span>
                  <span className="dot" />
                </span>
              </Cell>
            );
          })}
        </HeatGrid>
        <HeatDetail>
          <h4>{a.name}</h4>
          <div className="sub">{subtitle(a)}</div>
          <div style={{ marginBottom: '12px' }}>
            <Pill className={a.status}>
              <span className="d" />
              {statusLabel(a.status)}
            </Pill>
          </div>
          <HealthBar attributes={a} />
          <DRow>
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              {swatch('var(--good-bright)')}
              {t`Succeeded`}
            </span>
            <span>{a.succeeded}</span>
          </DRow>
          <DRow>
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              {swatch('var(--danger-bright)')}
              {t`Failed`}
            </span>
            <span>{a.failed}</span>
          </DRow>
          <DRow>
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              {swatch('var(--info-bright)')}
              {t`Blocked`}
            </span>
            <span>{a.blocked}</span>
          </DRow>
          <DRow>
            <span>{t`Last success`}</span>
            <span>
              {a.last_success_at ? timeAgo(a.last_success_at) : t`Never`}
            </span>
          </DRow>
          <DRow>
            <span>{t`Reason`}</span>
            <span>{buildReason(a)}</span>
          </DRow>
          <div style={{ marginTop: '12px' }}>
            <CardLink
              href={`/integrations/${selected.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t`Open integration ↗`}
            </CardLink>
          </div>
        </HeatDetail>
      </HeatLayout>
    </>
  );
}

HealthHeatmap.propTypes = {
  rows: healthRowsPropType.isRequired,
  loading: PropTypes.bool.isRequired,
  emptyState: PropTypes.node.isRequired,
};
